import React, { useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQuery } from '@tanstack/react-query'
import { societyFAQSectionCreateSchema } from '../../../../../forms/schemas/society_faq_section_create'
import { ToastType } from '../../../../common/Toast/toast-type'
import { isUserAdminInSociety } from '../../../../../helpers/society'
import { useFormErrorMessage } from '../../../../../hooks/useFormErrorMessage'
import { useToastNotifications } from '../../../../../hooks/useToastNotification'
import { useCurrentSociety } from '../../../../../hooks/useCurrentSociety'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { TextInput } from '../../../../common/TextInput'
import { FormControl } from '../../../../common/FormControl'
import { Button, ButtonVariant } from '../../../../common/Button'
import { faqQueries } from '../../../../../api/faqs'
import { useAuth } from '../../../../../hooks/useAuth'

interface CreateUpdateQuestionsAndAnswersSectionFormProps {
  onError: (error: unknown) => void
  onSubmit: (
    data: InferType<typeof societyFAQSectionCreateSchema>
  ) => Promise<void>
  onClose: () => void
  loading: boolean
  sectionId?: string
}

export const CreateUpdateQuestionsAndAnswersSectionForm = ({
  sectionId,
  loading,
  onError,
  onSubmit,
  onClose,
}: CreateUpdateQuestionsAndAnswersSectionFormProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const { setToastNotification } = useToastNotifications()
  const { userId } = useAuth()
  const { getErrorMessage } = useFormErrorMessage()
  const { data: sections } = useQuery(
    faqQueries.getFaqsPerSociety(society?._id)
  )
  const section = sections?.find((_section) => _section._id === sectionId)

  const updateMode = !!sectionId

  const isAdmin = isUserAdminInSociety(society, userId as string)

  if (!isAdmin || (updateMode && sectionId === undefined)) {
    onClose()
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const getDefaultValues = useCallback((): InferType<
    typeof societyFAQSectionCreateSchema
  > => {
    return {
      section: updateMode ? section?.section || '' : '',
    }
  }, [section, updateMode])

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societyFAQSectionCreateSchema),
    defaultValues: getDefaultValues(),
  })

  return (
    <>
      <Controller
        control={control}
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            label={translate(
              'createUpdateQuestionsAndAnswersSectionForm.labels.section'
            )}
            error={errors.section && getErrorMessage(errors.section)}
            name={name}
          >
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.section}
            />
          </FormControl>
        )}
        name="section"
      />
      <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-3">
        <Button
          label={translate('common.actions.cancel')}
          onClick={onClose}
          variant={ButtonVariant.TEXT}
        />
        <Button
          variant={ButtonVariant.PRIMARY}
          disabled={!isValid || loading}
          label={translate(
            updateMode ? 'common.actions.save' : 'common.actions.add'
          )}
          type="submit"
          onClick={handleSubmit(onSubmit, onError)}
        />
      </div>
    </>
  )
}
